import type { MouseEvent as ReactMouseEvent } from "react";
import type { HeadsectionStats } from "@/types";
import { TaskStatus } from "@/types";
import { TASK_STATUS_COLORS, TASK_STATUS_ICONS } from "@/lib/taskStatusMeta";

const CompletedIcon = TASK_STATUS_ICONS[TaskStatus.COMPLETED];
const InProgressIcon = TASK_STATUS_ICONS[TaskStatus.IN_PROGRESS];
const PendingIcon = TASK_STATUS_ICONS[TaskStatus.PENDING];
const ProblemIcon = TASK_STATUS_ICONS[TaskStatus.PROBLEM];

interface HeadsectionColumnProps {
  stats: HeadsectionStats;
  /**
   * When provided, the column renders as a `<button>` with hover/focus
   * affordances and forwards clicks up along with the stats for this section.
   */
  onClick?: (stats: HeadsectionStats, e: ReactMouseEvent) => void;
}

export function HeadsectionColumn({ stats, onClick }: HeadsectionColumnProps) {
  const {
    total,
    completed,
    inProgress,
    pending,
    problem,
    completedPercent,
    inProgressPercent,
    problemPercent,
    label,
  } = stats;
  const hasData = total > 0;
  const interactive = !!onClick;

  const body = (
    <>
      <div
        className="flex items-center gap-2 text-xs font-medium text-muted-foreground min-w-0"
        title={label}
      >
        <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground shrink-0">
          {stats.headsectionNumber}
        </span>
        <span className="truncate">{label}</span>
      </div>

      <div className={`h-2 w-full rounded-full overflow-hidden flex ${TASK_STATUS_COLORS[TaskStatus.PENDING].track}`}>
        {hasData && (
          <>
            {completedPercent > 0 && (
              <div
                className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.COMPLETED].bar}`}
                style={{ width: `${completedPercent}%` }}
              />
            )}
            {inProgressPercent > 0 && (
              <div
                className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.IN_PROGRESS].bar}`}
                style={{ width: `${inProgressPercent}%` }}
              />
            )}
            {problemPercent > 0 && (
              <div
                className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.PROBLEM].bar}`}
                style={{ width: `${problemPercent}%` }}
              />
            )}
          </>
        )}
      </div>

      {hasData ? (
        <div
          className="text-xs text-muted-foreground flex items-center justify-between gap-2 tabular-nums"
          aria-label={`${completed} valmis, ${inProgress} työn alla, ${pending} aloittamatta, ${problem} odottaa aliurakoitsijaa (${completedPercent.toFixed(0)}% valmis)`}
        >
          <div className="flex gap-x-1.5 min-w-0">
            <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${TASK_STATUS_COLORS[TaskStatus.COMPLETED].text}`}>
              {completed}
              <CompletedIcon className="h-3 w-3" aria-hidden />
            </span>
            <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${TASK_STATUS_COLORS[TaskStatus.IN_PROGRESS].text}`}>
              {inProgress}
              <InProgressIcon className="h-3 w-3" aria-hidden />
            </span>
            <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${TASK_STATUS_COLORS[TaskStatus.PROBLEM].text}`}>
              {problem}
              <ProblemIcon className="h-3 w-3" aria-hidden />
            </span>
            <span className={`inline-flex items-center gap-0.5 whitespace-nowrap ${TASK_STATUS_COLORS[TaskStatus.PENDING].text}`}>
              {pending}
              <PendingIcon className="h-3 w-3" aria-hidden />
            </span>
          </div>
          {/* Completed percentage — pushed flush to the right edge of the
              progress bar above via `justify-between` on the parent. */}
          <span className={`font-medium shrink-0 ${TASK_STATUS_COLORS[TaskStatus.COMPLETED].text}`}>
            {completedPercent.toFixed(0)}%
          </span>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground/50">Ei tehtäviä</div>
      )}
    </>
  );

  if (interactive) {
    // Negative margins + matching padding expand the hit area slightly past
    // the visible content without changing where the column sits on the grid.
    return (
      <button
        type="button"
        onClick={(e) => onClick!(stats, e)}
        aria-label={`Lohko ${stats.headsectionNumber}${label ? ` – ${label}` : ""}`}
        className="flex flex-col gap-1.5 min-w-0 text-left rounded-md -mx-1.5 -my-1 px-1.5 py-1 cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {body}
      </button>
    );
  }

  return <div className="flex flex-col gap-1.5 min-w-0">{body}</div>;
}
